import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import { formatShortDate } from "@progressive-development/pd-utils";
//#region src/pd-trash/PdTrashItem.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var PdTrashItem = class PdTrashItem extends LitElement {
	constructor(..._args) {
		super(..._args);
		this._expanded = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;
      container-type: inline-size;
    }

    .body {
      min-width: 0;
      padding: 0.6rem 0;
    }

    .title-line {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .title {
      font-weight: 600;
      color: var(--pd-default-font-title-col, inherit);
    }

    /* Technical originalId (no resolver/title) — visibly technical. */
    .title--technical {
      font-family: monospace;
      font-weight: 500;
    }

    time,
    .sub-line,
    .detail {
      font-size: var(--pd-trash-font-size-sub, 0.85em);
      color: var(--pd-default-font-muted-col, #757575);
    }

    time {
      white-space: nowrap;
    }

    button {
      font: inherit;
      border: none;
      background: none;
      padding: 0.25rem 0;
      margin: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      cursor: pointer;
      color: var(--pd-default-font-link-col, inherit);
      font-size: var(--pd-trash-font-size-sub, 0.85em);
      min-height: 2rem;
    }

    button:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col, currentColor);
      outline-offset: 2px;
    }

    .chevron {
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(90deg);
    }

    .detail {
      margin: 0.25rem 0 0.5rem;
    }

    .meta {
      display: grid;
      grid-template-columns: max-content 1fr;
      gap: 0.15rem 1rem;
    }

    .meta-label {
      color: var(--pd-default-font-muted-col, #757575);
    }

    .meta-value {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    @container (max-width: 480px) {
      .title-line time {
        flex-basis: 100%;
      }

      .meta {
        grid-template-columns: 1fr;
        gap: 0.1rem;
      }

      .meta-label {
        margin-top: 0.35rem;
      }
    }
  `;
	}
	render() {
		const entry = this.entry;
		if (!entry) return nothing;
		const title = this.titleResolver?.(entry);
		return html`
      <div class="body">
        <div class="title-line">
          ${title ? html`<span class="title">${title}</span>` : html`<span class="title title--technical"
                  >${entry.originalId}</span
                >`}
          <time datetime="${entry.deletedAt}" title="${entry.deletedAt}">
            ${msg(str`gelöscht ${formatShortDate(entry.deletedAt, true)}`, { id: "pd.trash.deleted" })}
          </time>
        </div>
        <div class="sub-line">
          ${this.typeLabels?.[entry.objectType] ?? entry.objectType} ·
          <span title="${entry.deletedBy}">
            ${msg(str`von ${this._actorText(entry)}`, { id: "pd.trash.by" })}
          </span>
        </div>
        <button
          aria-expanded="${this._expanded}"
          aria-controls="detailId"
          @click="${() => this._expanded = !this._expanded}"
        >
          <span
            class="chevron ${this._expanded ? "open" : ""}"
            aria-hidden="true"
            >▸</span
          >
          ${msg("Details", { id: "pd.trash.details" })}
        </button>
        ${this._expanded ? this._renderDetails(entry) : nothing}
      </div>
    `;
	}
	/** Actor display: denormalized name → shortened UID (full in title). */
	_actorText(entry) {
		if (entry.deletedByName) return entry.deletedByName;
		return entry.deletedBy.length > 8 ? `${entry.deletedBy.slice(0, 8)}…` : entry.deletedBy;
	}
	/** The original document as key-value rows — what would be restored. */
	_renderDetails(entry) {
		return html`
      <div class="detail" id="detailId">
        <div class="meta">
          ${Object.entries(entry.data).map(([key, value]) => html`
              <span class="meta-label">${this.fieldLabels?.[key] ?? key}</span>
              <span class="meta-value">${this._formatValue(key, value)}</span>
            `)}
        </div>
      </div>
    `;
	}
	_formatValue(field, value) {
		const custom = this.valueFormatter?.(field, value);
		if (custom !== void 0) return custom;
		if (value === null || value === void 0 || value === "") return "—";
		if (typeof value === "object") return JSON.stringify(value);
		return String(value);
	}
};
__decorate([property({ attribute: false })], PdTrashItem.prototype, "entry", void 0);
__decorate([property({ attribute: false })], PdTrashItem.prototype, "titleResolver", void 0);
__decorate([property({ attribute: false })], PdTrashItem.prototype, "typeLabels", void 0);
__decorate([property({ attribute: false })], PdTrashItem.prototype, "fieldLabels", void 0);
__decorate([property({ attribute: false })], PdTrashItem.prototype, "valueFormatter", void 0);
__decorate([state()], PdTrashItem.prototype, "_expanded", void 0);
PdTrashItem = __decorate([localized()], PdTrashItem);
//#endregion
export { PdTrashItem };
